import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogCustom from 'commons/DialogCustom';
import Text from 'commons/Text';
import Colors from 'configs/Colors';

import useStyles from './styles';

const ExtendShareLinkDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  defaultValue,
  defaultExpirationType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [expiresAt, setExpiresAt] = useState(defaultValue ? moment(defaultValue) : null);
  const [isPermanent, setIsPermanent] = useState(false);

  useEffect(() => {
    if (!open) return;
    const isNever = defaultExpirationType === 'never' || defaultExpirationType === 'permanent';
    setIsPermanent(isNever);
    setExpiresAt(!isNever && defaultValue ? moment(defaultValue) : null);
  }, [open, defaultValue, defaultExpirationType]);

  const canSubmit = useMemo(() => {
    if (isPermanent) return true;
    return !!expiresAt && moment(expiresAt).isValid();
  }, [expiresAt, isPermanent]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleSubmit = () => {
    if (!canSubmit || loading) return;

    if (isPermanent) {
      onConfirm?.({ expiration_type: 'permanent', expires_at: null });
      return;
    }

    const m = moment(expiresAt);
    onConfirm?.({ expiration_type: 'custom', expires_at: m.toISOString() });
  };

  return (
    <DialogCustom
      isShowDialog={open}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      dialogTitle={t('extend_link') || 'Extend link'}
      textSubmit={t('extend') || 'Extend'}
      textCancel=""
      maxWidth="sm"
      loading={loading}
      disabledButton={!canSubmit}
      PaperProps={{ className: classes.paper }}
      dialogHeaderStyle={classes.dialogHeader}
      dialogContentStyle={classes.dialogContent}
      dialogActionStyle={classes.dialogActions}
      submitButtonStyle={classes.submitButton}
      typeDialogTitle="lg"
    >
      <Box className={classes.form}>
        <Box className={classes.fieldBlock}>
          <Box className={classes.labelRow}>
            <Text className={classes.label}>{t('extend_to') || 'Extend to'}</Text>
            <Text className={classes.requiredMark}>*</Text>
          </Box>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              value={expiresAt}
              onChange={(v) => setExpiresAt(v)}
              disabled={isPermanent}
              slotProps={{
                textField: {
                  placeholder: t('select_date') || 'Select date',
                  fullWidth: true,
                  className: classes.input,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <FormControlLabel
          className={classes.checkboxRow}
          control={
            <Checkbox
              checked={isPermanent}
              onChange={(e) => {
                const next = e.target.checked;
                setIsPermanent(next);
                if (next) {
                  setExpiresAt(null);
                }
              }}
              sx={{
                padding: 0,
                marginRight: '8px',
                color: Colors.Grey4,
                '&.Mui-checked': {
                  color: Colors.Black,
                },
              }}
            />
          }
          label={
            <Text className={classes.checkboxLabel}>
              {t('make_link_permanent') || 'Make link permanent'}
            </Text>
          }
        />
      </Box>
    </DialogCustom>
  );
};

export default ExtendShareLinkDialog;
